// Utils
import { isValidNumber } from '@helpers/index';
// Types
import { CiiSimulatorFormValues } from '@schemas/forms';
import { FormikValues } from 'formik';

/**
 * Calculates the complementary value for a given percentage.
 * If the value is less than 0, returns 100. If the value is greater than 100, returns 0.
 * Otherwise, returns 100 minus the input value.
 *
 * @function
 * @param value - The percentage value to calculate the complementary value for.
 *
 * @returns The complementary percentage value.
 */
const calculatePairValue = (newValue = '') => {
  if (isValidNumber(newValue)) {
    if (Number(newValue) < 0) {
      return '100';
    }
    if (Number(newValue) > 100) {
      return '0';
    }
    return `${100 - Number(newValue)}`;
  }

  return '100';
};

/**
 * If the numeric string is valid and have a leading cero, it removes it.
 *
 * @function
 * @param value - The numeric value string.
 *
 * @returns The complementary percentage value.
 */
const trimLeadingCero = (newValue = '') => (isValidNumber(newValue) ? `${Number(newValue)}` : newValue);

/**
 * Updates the percentage value of a specific fuel mode in a Formik context.
 *
 * @function
 * @param formikCtx - The Formik context containing the current form values and methods.
 * @param index - The index of the fuel mode to be updated.
 * @param newValue - The new percentage value for the fuel mode. If not provided,
 * the function will calculate the complementary value based on the existing value.
 */
export const setFuelModeGroupValues = (formikCtx: FormikValues, index: number, newValue = '') => {
  const prevPercentages = (formikCtx.values as CiiSimulatorFormValues).fuelModes.map(({ percentage }) => percentage);
  const newValues = { ...formikCtx.values };

  if (index === 0 && newValue !== prevPercentages[0]) {
    newValues.fuelModes = [
      { ...formikCtx.values.fuelModes[0], percentage: trimLeadingCero(newValue) },
      { ...formikCtx.values.fuelModes[1], percentage: calculatePairValue(newValue) },
    ];
  }

  if (index === 1 && newValue !== prevPercentages[1]) {
    newValues.fuelModes = [
      { ...formikCtx.values.fuelModes[0], percentage: calculatePairValue(newValue) },
      { ...formikCtx.values.fuelModes[1], percentage: newValue },
    ];
  }

  formikCtx.setValues({ ...newValues });
};
